import { RouteResponse } from "@0xsquid/squid-types";
import {
  SwapTransactionForStatus,
  TransactionParams,
  TransactionStatus,
} from "../../core/types/transaction";
import { useTransactionStore } from "../store/useSquidStore";
import { useSwap } from "../swap/useSwap";
import { useHistory } from "../user/useHistory";
import { useSwapStatusQuery } from "./useSwapStatusQuery";

interface UseDepositTransactionStatusParams {
  transaction?: SwapTransactionForStatus;
  route?: RouteResponse["route"];
  retry?: number;
  refetchOnWindowFocus?: "always" | boolean;
  enabled?: boolean;
  onReceived?: () => void;
}

// Statuses that indicate the source deposit has been received
// and the swap is now progressing.
const sourceReceivedStatuses = [
  // Chainflip
  "DEPOSIT_RECEIVED",
  "BROADCAST_REQUESTED",
  "COMPLETE",
  "SWAPPING",
  // Canton (Squid Intents)
  "awaiting",
  "success",
];

/**
 * Tracks a deposit address intent before it becomes a persisted swap history item.
 *
 * Once the source deposit is received, it registers the transaction in the transaction
 * and history stores, then signals the view via `onReceived` to navigate.
 */
export const useDepositTransactionStatus = ({
  transaction,
  route,
  retry = 25,
  refetchOnWindowFocus = "always",
  enabled = true,
  onReceived,
}: UseDepositTransactionStatusParams) => {
  const { fromChain, toChain } = useSwap();
  const { addSwapTransaction } = useHistory();
  const getTransaction = useTransactionStore((state) => state.getTransaction);
  const setTransactionStoreState = useTransactionStore(
    (state) => state.setTransactionState
  );

  return useSwapStatusQuery({
    transaction,
    retry,
    refetchOnWindowFocus,
    enabled,
    onStatus: ({ status }) => {
      if (!sourceReceivedStatuses.includes(status)) return;
      if (!transaction?.transactionId || !route?.transactionRequest) return;

      const { transactionId } = transaction;

      if (getTransaction(transactionId)) return;

      useTransactionStore.setState({
        txLocalId: transactionId,
        currentTransaction: undefined,
      });

      const tx: TransactionParams = {
        routeType: route.transactionRequest.type,
        fromChain,
        toChain,
        fromAddress: transaction.fromAddress,
        transactionId,
        transactionIdForStatus: transaction.transactionIdForStatus,
        quoteId: transaction.quoteId ?? "",
        status: TransactionStatus.ONGOING,
        sourceStatus: TransactionStatus.SUCCESS,
        timestamp: Date.now(),
      };

      setTransactionStoreState(transactionId, tx);
      addSwapTransaction({
        ...tx,
        params: route.params,
        estimate: route.estimate,
      });

      onReceived?.();
    },
  });
};
